import { useCallback, useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useSearchParams from '@/hooks/useSearchParam';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import { getLearnerProfileTabs, LearnerProfileTabsEnum, TAB_SEARCH_PARAM } from './utils';

interface Props {
  learnerId: string;
}

const useLearnerProfileTabs = ({ learnerId }: Props) => {
  const { t } = useTranslation();
  const tab = useSearchParams(TAB_SEARCH_PARAM);
  const { isMobile } = useScreen();
  const {
    [FeatureFlags.EnableActivity]: isActivityEnabled,
    [FeatureFlags.EnableOKR]: isOKREnabled,
    [FeatureFlags.EnableOneOnOne]: is1on1Enabled,
    [FeatureFlags.EnableOverviewTab]: isOverviewTabEnabled,
    [FeatureFlags.EnableBadges]: isBadgesEnabled,
    [FeatureFlags.EnablePoints]: isPointsEnabled,
    [FeatureFlags.EnablePerformanceReviews]: isPerformanceReviewsEnabled,
    [FeatureFlags.EnableLearnModule]: isLearnModuleEnabled,
    [FeatureFlags.EnablePerformModule]: isPerformModuleEnabled,
    [FeatureFlags.EnableEngageModule]: isEngageModuleEnabled,
  } = useFeatureFlags();
  const isAdmin = useAdminRoute();

  const isLearnerProfileTabEnabled: (tab: LearnerProfileTabsEnum) => boolean = useCallback(
    (tab) => {
      switch (tab) {
        // Engagement module
        case LearnerProfileTabsEnum.OneOnOne:
          return isEngageModuleEnabled && !!is1on1Enabled && !isMobile;

        // Performance module
        case LearnerProfileTabsEnum.OKRs:
          return !!isPerformModuleEnabled && !!isOKREnabled && !isMobile;
        case LearnerProfileTabsEnum.Skills:
          return true;
        case LearnerProfileTabsEnum.Mindsets:
          return true;
        case LearnerProfileTabsEnum.SkillReviews:
          return !!isPerformModuleEnabled && !!isPerformanceReviewsEnabled && !isMobile;
        case LearnerProfileTabsEnum.Activities:
          return !!isPerformModuleEnabled && !!isActivityEnabled && !isMobile;
        case LearnerProfileTabsEnum.Overview:
          return !!isLearnModuleEnabled && !!isOverviewTabEnabled;
        case LearnerProfileTabsEnum.Badges:
          return isLearnModuleEnabled && !!isBadgesEnabled;

        // Learn module
        case LearnerProfileTabsEnum.Points:
          return isLearnModuleEnabled && !!isPointsEnabled;
        case LearnerProfileTabsEnum.Certificates:
          return isLearnModuleEnabled;
        case LearnerProfileTabsEnum.Content:
          return isLearnModuleEnabled;
        case LearnerProfileTabsEnum.Sessions:
          return isLearnModuleEnabled;

        // Common moudule
        case LearnerProfileTabsEnum.About:
          return isMobile;
        default:
          return true;
      }
    },
    [
      is1on1Enabled,
      isActivityEnabled,
      isBadgesEnabled,
      isEngageModuleEnabled,
      isLearnModuleEnabled,
      isMobile,
      isOKREnabled,
      isOverviewTabEnabled,
      isPerformModuleEnabled,
      isPerformanceReviewsEnabled,
      isPointsEnabled,
    ],
  );
  const tabs = useMemo(
    () => getLearnerProfileTabs({ t, learnerId, isAdmin, isLearnerProfileTabEnabled }),
    [isAdmin, learnerId, t, isLearnerProfileTabEnabled],
  );

  const activeTab = useMemo(
    () => tabs.find(({ value }) => value === tab)?.value || tabs[0].value,
    [tab, tabs],
  );

  return { activeTab, tabs };
};

export default useLearnerProfileTabs;
